// Light, dark, or whatever the device is set to — one saved choice per device.
//
// index.css carries both themes: :root is light, the prefers-color-scheme block
// is the device's dark, and :root[data-theme] is an explicit choice that wins
// either way. This module is the only thing that sets that attribute: main.tsx
// shows the saved choice before the first render, and the office sidebar's
// switch makes a new one.
//
// ⚠ "Auto" IS THE ABSENCE OF A CHOICE, NEVER A STORED VALUE. It removes the
// attribute and the saved key. Writing a default into storage would stamp every
// new device with it for ever and make the device-dark branch of index.css
// unreachable — once set, the attribute always wins.

export type ThemeChoice = 'light' | 'dark' | 'auto';

const THEME_KEY = 'quality:theme';

/** The page ground in each theme: index.html's theme-color values, which paint
 *  the iPad status bar. A choice that overrides the device must repaint it. */
const GROUND = { light: '#f1ece9', dark: '#22252a' } as const;

export function savedTheme(): ThemeChoice {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'auto';
  } catch {
    // Storage can be blocked entirely (private window, locked-down iPad profile).
    // Following the device is a fine answer; failing to boot is not.
    return 'auto';
  }
}

/** Put a choice on the page without remembering it. */
export function showTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    const own = (meta.getAttribute('media') ?? '').includes('dark') ? 'dark' : 'light';
    meta.setAttribute('content', GROUND[choice === 'auto' ? own : choice]);
  });
}

/** Make a choice: show it, and remember it on this device. */
export function chooseTheme(choice: ThemeChoice): void {
  showTheme(choice);
  try {
    if (choice === 'auto') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
  } catch {
    // Not remembered, but in force for this visit — still the right answer.
  }
}
